// Turn any API error into a sentence a member can read.
//
// ## Why this exists
//
// FastAPI answers a rejected request in two different shapes, and the
// difference is invisible until it reaches a screen:
//
//   * A business rule we raised ourselves — `HTTPException(400, detail="…")` —
//     puts a STRING in `detail`.
//   * A Pydantic field validator — a password that is too short, a malformed
//     email — answers **422**, and `detail` is an ARRAY of
//     `{loc, msg, type}` objects.
//
// `error.response?.data?.detail || 'fallback'` is truthy for both, so the array
// sails through and React renders `[object Object]`, or the caller wraps it in
// `new Error(detail)` and the message becomes the same. The user is told
// nothing at all.
//
// That is not hypothetical. A member in Yemen could not reset his password: the
// backend required a Latin letter, so every Arabic password he chose came back
// 422, and the reason — "the password must contain letters" — was replaced by
// noise. He read it as "the app does not respond to the password", tried again,
// and each retry burned an OTP attempt until he was rate-limited out of his own
// account. The Latin-only rule is fixed; this makes sure the next validation
// error actually reaches the person who has to act on it.
//
// The form pages (BrideFormPage, GroomFormPage, ProfileEditPage) already do
// this by hand, and better — they name which FIELD failed. Those are left
// alone; this is for the many call sites that had no array handling at all.

/**
 * Extract a human-readable message from an axios error.
 *
 * @param {unknown} error     the caught axios/Error object
 * @param {string} fallback   shown when the server said nothing useful
 * @returns {string}
 */
export function apiErrorMessage(error, fallback) {
  const detail = error?.response?.data?.detail;

  // The common case: we raised it, it is already a sentence.
  if (typeof detail === 'string' && detail.trim()) return detail.trim();

  // Pydantic 422. Join the messages rather than showing the first, because a
  // form can fail two rules at once and hiding one sends the user round again.
  if (Array.isArray(detail) && detail.length) {
    const messages = detail
      .map((item) => {
        if (typeof item === 'string') return item;
        // Pydantic v2 prefixes a custom ValueError with "Value error, ".
        // That prefix is for developers; strip it before it reaches a member.
        return String(item?.msg || item?.message || '')
          .replace(/^Value error,\s*/i, '')
          .trim();
      })
      .filter(Boolean);
    if (messages.length) return [...new Set(messages)].join(' — ');
  }

  // Some endpoints answer `{message: "..."}` instead of `{detail: ...}`.
  const message = error?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) return message.trim();

  // A transport failure has no response at all; axios' own message ("Network
  // Error") is not something to show, so prefer the caller's fallback.
  return fallback;
}

export default apiErrorMessage;
